'use client';

import { useTranslations } from 'next-intl';

import { Amenity, AmenityCode } from '@/components/Amenity';
import { ImageWithPlaceholder } from '@/components/ImageWithPlaceholder';
import { Link } from '@/components/Link';
import LocationOpeningLabel from '@/components/LocationOpeningLabel';
import { useLocationImageOrPlaceholder } from '@/hooks/useLocationImagePlaceholder';
import { ScreenSize, useScreenSize } from '@/hooks/useScreenSize';
import { LocationPhases, SimpleLocationDto } from '@/lib/locations';
import { getFormattedAddressByCountry } from '@/models/location-utils';

type NearbyLocationItemProps = {
  location: SimpleLocationDto;
};

const MAX_AMENITIES_DISPLAY = 4;
const MOBILE_MAX_AMENITIES_DISPLAY = 2;

export default function NearbyLocationItem({
  location,
}: NearbyLocationItemProps) {
  const t = useTranslations('location.nearby-locations');
  const locationUrl = `/locations/${location.slug}`;
  const image = useLocationImageOrPlaceholder(location.image);
  const screenSize = useScreenSize();

  const showOpeningLabel = [
    LocationPhases.Signed,
    LocationPhases.Launch,
  ].includes(location.phase);

  const formattedAddress = getFormattedAddressByCountry({
    country: location.country,
    address: location.address,
    address2: location.address2,
    city: location.city,
    state: location.state,
    zipCode: location.zipCode,
  });

  const amenities = location.amenities?.unique;

  const isMobile = screenSize < ScreenSize.SM;

  const maxAmenitiesDisplay = isMobile
    ? MOBILE_MAX_AMENITIES_DISPLAY
    : MAX_AMENITIES_DISPLAY;

  return (
    <Link href={locationUrl}>
      <div className="flex flex-col h-full bg-white rounded-lg md:flex-row">
        <ImageWithPlaceholder
          className="object-cover object-center rounded-lg w-full md:max-w-[40%] md:w-[320px]"
          width="320"
          height="252"
          src={image.src}
          alt={image.alt || t('alt-default-image')}
          sizes="(max-width: 640px) 100vw, 320px"
        />

        <div className="flex flex-col justify-center flex-1 py-4 text-sm md:p-6 md:pt-8">
          {showOpeningLabel && location.firstDayOfBusinessISO && (
            <LocationOpeningLabel
              className="text-sm not-italic font-normal text-grey-5"
              openingDateISO={location.firstDayOfBusinessISO}
              locationGeolocalization={location.geolocalization}
            />
          )}

          <div className="flex items-center justify-between gap-2">
            <h4
              className="flex-1 text-base font-bold truncate md:text-lg text-ocean-60"
              data-testid="location-name"
            >
              {location.name}
            </h4>
          </div>
          <div className="flex flex-wrap gap-2 text-sm text-grey-5">
            <p>
              {formattedAddress.addressLine1} {formattedAddress.addressLine2}
            </p>
            {location.phone && (
              <>
                <span>|</span>
                <p>{location.phone}</p>
              </>
            )}
          </div>
          {amenities && amenities.length > 0 && (
            <div className="grid grid-cols-2 gap-2.5 mt-5 text-sm xl:w-[55%]">
              {amenities.slice(0, maxAmenitiesDisplay).map((amenity, i) => (
                <Amenity
                  key={amenity?.code}
                  code={amenity?.code as AmenityCode}
                  name={amenity?.label}
                  labelClassName="text-grey-25"
                  iconClassName="text-grey-25 flex-shrink-0"
                >
                  {i === maxAmenitiesDisplay - 1 &&
                    amenities.length > maxAmenitiesDisplay && (
                      <span className="pl-1 text-grey-25">
                        {t('and-more-amenities', {
                          count: amenities.length - maxAmenitiesDisplay,
                        })}
                      </span>
                    )}
                </Amenity>
              ))}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
